import type { BlobUploadProgress } from '../api/blobUpload';
import { applyI18n } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { iconImg, type IconName } from './icons';
import { applyUploadProgress } from './uploadProgress';

export interface CryptoBusyOptions {
  titleKey: string;
  detailKey: string;
  icon?: IconName;
}

const STATUS_ID = 'vault-crypto-status';
const activeBusyPanels = new Set<HTMLElement>();

bindLocaleRefresh(() => {
  for (const status of activeBusyPanels) {
    applyI18n(status);
  }
});

export function setCryptoBusy(
  panel: HTMLElement,
  scope: HTMLFormElement | HTMLElement,
  busy: boolean,
  options: CryptoBusyOptions,
): void {
  let status = panel.querySelector(`#${STATUS_ID}`) as HTMLElement | null;

  if (busy) {
    if (!status) {
      status = document.createElement('div');
      status.id = STATUS_ID;
      status.className = 'crypto-busy-panel';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-busy', 'true');

      const spinner = document.createElement('span');
      spinner.className = 'crypto-busy-panel__spinner';
      spinner.setAttribute('aria-hidden', 'true');

      const text = document.createElement('div');
      text.className = 'crypto-busy-panel__text';
      const title = document.createElement('strong');
      title.setAttribute('data-i18n', options.titleKey);
      const detail = document.createElement('p');
      detail.className = 'muted';
      detail.setAttribute('data-i18n', options.detailKey);
      const progressLabel = document.createElement('p');
      progressLabel.className = 'muted crypto-busy-panel__progress-label';
      progressLabel.hidden = true;
      const progress = document.createElement('progress');
      progress.className = 'crypto-busy-panel__progress';
      progress.hidden = true;
      progress.max = 100;
      progress.value = 0;
      text.append(title, detail, progressLabel, progress);

      const iconSlot = document.createElement('span');
      iconSlot.className = 'crypto-busy-panel__icon';
      if (options.icon) {
        iconSlot.append(iconImg(options.icon, 24));
      }

      status.append(spinner, iconSlot, text);
      scope.before(status);
      activeBusyPanels.add(status);
    } else {
      status.querySelector('strong')?.setAttribute('data-i18n', options.titleKey);
      status.querySelector('p')?.setAttribute('data-i18n', options.detailKey);
    }

    for (const element of scope.querySelectorAll('input, button, select, textarea')) {
      if (element instanceof HTMLInputElement || element instanceof HTMLButtonElement) {
        element.disabled = true;
      }
    }
    scope.setAttribute('aria-busy', 'true');
    applyI18n(status);
    status.hidden = false;
    return;
  }

  status?.remove();
  if (status) {
    activeBusyPanels.delete(status);
  }
  for (const element of scope.querySelectorAll('input, button, select, textarea')) {
    if (element instanceof HTMLInputElement || element instanceof HTMLButtonElement) {
      element.disabled = false;
    }
  }
  scope.removeAttribute('aria-busy');
}

export function setCryptoBusyProgress(panel: HTMLElement, progress: BlobUploadProgress | null): void {
  const status = panel.querySelector(`#${STATUS_ID}`) as HTMLElement | null;
  const progressEl = status?.querySelector('.crypto-busy-panel__progress') as HTMLProgressElement | null;
  const progressLabel = status?.querySelector('.crypto-busy-panel__progress-label') as HTMLElement | null;
  if (!progressLabel || !progressEl) {
    return;
  }

  if (!progress) {
    progressLabel.hidden = true;
    progressEl.hidden = true;
    return;
  }

  applyUploadProgress(progressLabel, progressEl, progress);
}

